import type { Theme } from "@mui/material/styles";
import type { SxProps } from "@mui/material";

export const touchScrollbarStyles = (theme: Theme): SxProps<Theme> => ({
  "&::-webkit-scrollbar": {
    width: "40px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.grey[100],
    borderLeft: `1px solid ${theme.palette.grey[300]}`,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.grey[500],
    border: `7px solid ${theme.palette.grey[100]}`,
    borderRadius: "16px",
  },
  "&::-webkit-scrollbar-button:single-button": {
    display: "block",
    height: "44px",
    backgroundColor: theme.palette.grey[200],
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "28px 28px",
  },
  "&::-webkit-scrollbar-button:single-button:vertical:decrement": {
    backgroundImage:
      'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2228%22 height=%2228%22 viewBox=%220 0 28 28%22%3E%3Cpath d=%22M5 18l9-9 9 9%22 fill=%22none%22 stroke=%22%234250b5%22 stroke-width=%223.5%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22/%3E%3C/svg%3E")',
  },
  "&::-webkit-scrollbar-button:single-button:vertical:increment": {
    backgroundImage:
      'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2228%22 height=%2228%22 viewBox=%220 0 28 28%22%3E%3Cpath d=%22M5 10l9 9 9-9%22 fill=%22none%22 stroke=%22%234250b5%22 stroke-width=%223.5%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22/%3E%3C/svg%3E")',
  },
});

/**
 * Charge Operation styles using theme colors
 * Usage: const theme = useTheme(); const styles = chargeOperationStyles(theme);
 * Then apply with sx prop: <Box sx={styles.root}>
 */
export const chargeOperationStyles = (
  theme: Theme,
): Record<string, SxProps<Theme>> => ({
  root: {
    height: "100vh",
    backgroundColor: theme.palette.grey[200],
    display: "flex",
    flexDirection: "column",
  },
  mainContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
    minHeight: 0,
    backgroundColor: theme.palette.grey[200],
  },
  sidebar: {
    width: "294px",
    minHeight: 0,
    alignSelf: "stretch",
    margin: theme.spacing(1),
    backgroundColor: "white",
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(0.75),
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    overflow: "hidden",
    flexShrink: 0,
    position: "relative",
    zIndex: 1,
    boxShadow: theme.shadows[4],
  },
  chargesScrollContainer: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    minHeight: 0,
  },
  sidebarSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    margin: theme.spacing(1),
    overflow: "hidden",
    backgroundColor: "transparent",
    borderRadius: 0,
    boxShadow: "none",
  },
  headerSection: {
    marginBottom: theme.spacing(1),
  },
  chargeInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(1),
  },
  chargeId: {
    fontSize: "1.4rem",
    fontWeight: 600,
    marginRight: theme.spacing(2),
  },
  operatorBucketBar: {
    display: "flex",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.charges.borders.medium}`,
  },
  infoItem: {
    flexGrow: 1,
    textAlign: "center",
    padding: theme.spacing(1),
    "&:not(:last-child)": {
      borderRight: `1px solid ${theme.palette.charges.borders.medium}`,
    },
  },
  infoLabel: {
    fontWeight: 500,
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  infoValue: {
    fontWeight: 600,
    fontSize: "1.8rem",
  },
  touchButton: {
    padding: theme.spacing(2),
    fontWeight: "bold",
    fontSize: "1.7rem",
    height: "80px",
    textTransform: "none",
    borderRadius: "8px",
  },
  headerTitle: {
    textAlign: "center",
    fontSize: "1.5rem",
    fontWeight: 600,
    flex: 1,
  },
  contentSection: {
    flex: 1,
    display: "flex",
    marginBottom: theme.spacing(1),
    overflow: "hidden",
    backgroundColor: theme.palette.grey[200],
  },
  leftPanel: {
    width: "55%",
    marginRight: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: theme.shadows[2],
    overflow: "hidden",
  },
  rightPanel: {
    width: "45%",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: theme.shadows[2],
    overflow: "hidden",
  },
  panelHeader: {
    position: "relative",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: "white",
    borderTopLeftRadius: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "52px",
  },
  panelTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
  },
  panelContent: {
    flex: 1,
    overflow: "hidden",
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
  },
  tableContainer: {
    flex: 1,
    overflow: "auto",
    height: "100%",
    ...touchScrollbarStyles(theme),
  },
  tableHeader: {
    backgroundColor: theme.palette.charges.backgrounds.light,
    "& th": {
      color: theme.palette.text.secondary,
      fontStyle: "italic",
      fontWeight: "bold",
      padding: "2px 4px",
      fontSize: "1rem",
      lineHeight: "24px",
      position: "sticky",
      top: 0,
      zIndex: 10,
      backgroundColor: theme.palette.charges.backgrounds.light,
      borderBottom: `1px solid ${theme.palette.charges.borders.medium}`,
    },
  },
  tableRow: {
    cursor: "pointer",
    height: "65px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& td": {
      padding: theme.spacing(2),
      fontSize: "1.2rem",
      borderBottom: `1px solid ${theme.palette.charges.borders.medium}`,
    },
    "& td:nth-of-type(1), & td:nth-of-type(3), & td:nth-of-type(4)": {
      fontSize: "1.5rem",
    },
  },
  currentLayersTableRow: {
    cursor: "pointer",
    height: "65px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& td": {
      padding: theme.spacing(2),
      fontSize: "1.2rem",
      borderBottom: `1px solid ${theme.palette.charges.borders.medium}`,
    },
    "& td:nth-of-type(1), & td:nth-of-type(3), & td:nth-of-type(4)": {
      fontSize: "1.5rem",
    },
  },
  selectedRow: {
    backgroundColor: theme.palette.action.selected,
    "& td:first-of-type": {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
    },
  },
  footerSection: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.charges.borders.medium}`,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    borderBottomLeftRadius: theme.spacing(1),
    borderBottomRightRadius: theme.spacing(1),
    minHeight: "80px",
  },
  weightDisplayRow: {
    padding: 0,
    margin: 0,
    backgroundColor: theme.palette.charges.backgrounds.light,
    borderTop: `1px solid ${theme.palette.charges.borders.medium}`,
  },
  weightInfoContainer: {
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  weightInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: theme.spacing(0.5),
  },
  weightTextExceeded: {
    fontFamily: "inherit",
    fontWeight: 700,
    color: "#ff7b72",
  },
  weightTextNormal: {
    fontWeight: 700,
    fontSize: "1.5rem",
    color: "white",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  weightLabel: {
    color: "rgba(255, 255, 255, 0.7)",
    fontWeight: 500,
    fontSize: "1rem",
  },
  weightDifference: {
    fontWeight: 700,
    fontSize: "1.5rem",
    color: theme.palette.error.dark,
    marginTop: theme.spacing(0),
  },
  positiveWeight: {
    color: theme.palette.success.main,
  },
  dangerColor: {
    color: theme.palette.error.dark,
  },
  successColor: {
    color: theme.palette.success.main,
  },
  addLayerBottomButton: {
    color: "white",
    backgroundColor: theme.palette.success.main,
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.charges.borders.medium,
      color: theme.palette.charges.status.inactive,
    },
    minWidth: 0,
    height: "80px",
    padding: theme.spacing(0.5),
    borderRadius: "8px",
    boxShadow: theme.shadows[2],
    fontSize: "1.1rem",
    fontWeight: 700,
  },
  editWeightButton: {
    color: "white",
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.success.main,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.charges.borders.medium,
      color: theme.palette.charges.status.inactive,
    },
    padding: theme.spacing(2, 3),
    fontSize: "1.3rem",
    minWidth: "180px",
    height: "60px",
    margin: theme.spacing(0.5),
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    flexShrink: 0,
  },
  saveWeightButton: {
    color: "white",
    backgroundColor: theme.palette.success.main,
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.charges.borders.medium,
      color: theme.palette.charges.status.inactive,
    },
    padding: theme.spacing(2, 3),
    fontSize: "1.3rem",
    minWidth: "120px",
    height: "60px",
    margin: theme.spacing(0.5),
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    flexShrink: 0,
  },
  cancelButton: {
    backgroundColor: theme.palette.charges.backgrounds.light,
    "&:hover": {
      backgroundColor: theme.palette.charges.borders.medium,
    },
  },
  cancelWeightButton: {
    color: "white",
    backgroundColor: theme.palette.grey[600],
    "&:hover": {
      backgroundColor: theme.palette.grey[700],
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.charges.borders.medium,
      color: theme.palette.charges.status.inactive,
    },
    padding: theme.spacing(2, 3),
    fontSize: "1.3rem",
    minWidth: "120px",
    height: "60px",
    margin: theme.spacing(0.5),
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    flexShrink: 0,
  },
  manualWeightInput: {
    margin: theme.spacing(0.5),
    minWidth: "160px",
    flexShrink: 0,
    "& .MuiOutlinedInput-root": {
      height: "60px",
      borderRadius: "8px",
      backgroundColor: "white",
      fontSize: "1.3rem",
      fontWeight: 600,
      "& input": {
        textAlign: "center",
        padding: "18px 16px",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: "2px",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.charges.borders.medium,
    },
  },
  deleteButton: {
    color: "white",
    backgroundColor: theme.palette.error.main,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.charges.borders.medium,
      color: theme.palette.charges.status.inactive,
    },
    padding: theme.spacing(2, 3),
    fontSize: "1.3rem",
    minWidth: "180px",
    height: "60px",
    margin: theme.spacing(1),
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
  },
  cancelCommentButton: {
    backgroundColor: theme.palette.charges.status.inactive,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.grey[600],
    },
  },
  saveCommentButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  chargeLayerFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.charges.borders.medium}`,
    borderBottomLeftRadius: theme.spacing(1),
    borderBottomRightRadius: theme.spacing(1),
    minHeight: "50px",
    gap: theme.spacing(2),
    "@media (max-width: 900px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  layerActionButtons: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    justifyContent: "flex-start",
    flex: "1 1 auto",
    minWidth: 0,
  },
  manualWeightGroup: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  weightDisplayContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    flexShrink: 0,
    marginTop: theme.spacing(2),

    "@media (max-width: 900px)": {
      justifyContent: "center",
      marginTop: theme.spacing(1),
    },
  },
  closeButton: {
    backgroundColor: theme.palette.charges.chargeTypes.closed,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  openButton: {
    backgroundColor: theme.palette.charges.status.active,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  commodityCell: {
    display: "flex",
    alignItems: "center",
  },
  colorIndicator: {
    width: "24px",
    height: "24px",
    borderRadius: "3px",
    marginRight: theme.spacing(1),
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "60px",
  },
  recipeSelector: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    padding: "0 16px",
    border: `1px solid ${theme.palette.charges.borders.medium}`,
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  recipeSelectorReadOnly: {
    cursor: "default",
    borderColor: theme.palette.grey[400],
    backgroundColor: theme.palette.grey[50],
    "&:hover": {
      backgroundColor: theme.palette.grey[50],
    },
  },
  recipeName: {
    fontSize: "1.2rem",
    fontWeight: 500,
  },
  bucketSelector: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    padding: theme.spacing(1, 2),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    minHeight: 48,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  bucketSelectorReadOnly: {
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  bucketName: {
    fontSize: "1.2rem",
    fontWeight: 500,
  },
  dialogPaper: {
    width: "500px",
    maxWidth: "90vw",
    borderRadius: "12px",
  },
  dialogHeader: {
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.charges.borders.medium}`,
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  dialogContent: {
    padding: theme.spacing(1),
    maxHeight: "60vh",
    overflowY: "auto",
  },
  recipeItem: {
    padding: theme.spacing(2),
    margin: theme.spacing(0.5, 0),
    border: `1px solid ${theme.palette.charges.borders.medium}`,
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    cursor: "pointer",
  },
  selectedRecipeItem: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
  },
  recipeText: {
    fontSize: "1.1rem",
  },
  bucketItem: {
    borderRadius: 8,
    margin: theme.spacing(0.5, 1),
    padding: theme.spacing(1.5, 2),
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedBucketItem: {
    backgroundColor: `${theme.palette.primary.light}20`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  bucketText: {
    fontWeight: 500,
    flexGrow: 1,
  },
  checkIcon: {
    color: theme.palette.primary.main,
  },
  dialogFooter: {
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.charges.borders.medium}`,
    display: "flex",
    justifyContent: "flex-end",
  },
  actionButton: {
    padding: theme.spacing(1.5, 3),
    minWidth: "180px",
    fontSize: "1.2rem",
    fontWeight: 600,
    borderRadius: "8px",
    minHeight: "56px",
    textTransform: "none",
    boxShadow: "0 3px 5px rgba(0,0,0,0.2)",
  },
  buttonWithIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
  diffCell: {
    color: theme.palette.success.main,
    fontWeight: "bold",
  },
  negativeDiff: {
    color: theme.palette.error.main,
    fontWeight: "bold",
  },
  centerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: theme.spacing(4),
  },
  actionButtons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(0.5),
  },
  editModeButton: {
    backgroundColor: theme.palette.warning.main,
    color: "white",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
  runModeButton: {
    backgroundColor: theme.palette.success.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  manualIndicator: {
    backgroundColor: theme.palette.charges.chargeTypes.manual,
    color: "white",
    borderRadius: "4px",
    padding: "4px 6px",
    display: "inline-block",
    fontWeight: "bold",
    fontSize: "1.2rem",
  },
  statusChip: {
    flex: 1,
    backgroundColor: theme.palette.primary.main,
    color: "white",
    border: `1px solid ${theme.palette.charges.borders.medium}`,
    borderRadius: "16px",
    fontSize: "1rem",
    padding: "4px 12px",
    height: "32px",
    fontWeight: 700,
    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.08)",
    cursor: "default",
    userSelect: "none",
  },
  statusClosed: {
    backgroundColor: theme.palette.charges.chargeTypes.closed,
  },
  statusOpen: {
    backgroundColor: theme.palette.success.dark,
  },
  statusEAF: {
    backgroundColor: theme.palette.grey[700],
  },
  targetWeight: {
    fontWeight: "bold",
    fontSize: "1.5rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    whiteSpace: "nowrap",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(4),
    gap: theme.spacing(2),
  },
  commentsInfoItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minHeight: 72,
    textAlign: "center",
    padding: theme.spacing(1),
    "&:not(:last-child)": {
      borderRight: `1px solid ${theme.palette.charges.borders.medium}`,
    },
  },
  commentsGrid: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 40,
  },
  commentsIconButton: {
    textDecoration: "none",
    fontWeight: 500,
    borderRadius: "4px",
    padding: 0,
    height: 40,
    width: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  commentsIcon: {
    fontSize: "3rem",
    display: "block",
  },
  newChargeButton: {
    backgroundColor: theme.palette.charges.status.active,
    color: "white",
    height: "64px",
    fontSize: "1.2rem",
    whiteSpace: "nowrap",
    borderRadius: "8px",
    fontWeight: 600,
    boxShadow: "0 3px 5px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "8px",
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.charges.borders.medium,
      color: theme.palette.charges.status.inactive,
    },
  },
  smallBadge: {
    "& .MuiBadge-badge": {
      right: 3,
      top: 5,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: "0 4px",
      fontSize: "0.7rem",
      height: "12px",
      minWidth: "12px",
      backgroundColor: `${theme.palette.success.main} !important`,
      color: "white",
    },
  },
  hiddenBadge: {
    "& .MuiBadge-badge": {
      display: "none",
    },
  },
});
